import React from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import ball from '../assets/images/ballLunge.jpg';
import kbSquat from '../assets/images/kettlebellSquat.jpg';

function Training() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <SEO
        title={t('seo.training.title')}
        description={t('seo.training.description')}
        path="/training"
      />
      <Navbar />

      <main className="flex-grow max-w-7xl mx-auto px-6 lg:px-12 py-6 lg:py-20 text-left">

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-[#162E3B] text-center mb-8">
          {t('training.title')}
        </h1>

        {/* Intro */}
        <div className="space-y-6 mb-12">
          <p className="text-base sm:text-lg text-gray-800 font-semibold leading-relaxed">
            {t('training.intro1')}
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            {t('training.intro2')}
          </p>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            {t('training.intro3')}
          </p>
        </div>

        {/* Grid / Content Blocks */}
        <div className="space-y-12 lg:space-y-16">

          {/* Strength & Muscle Mass with Kettlebell Squat image */}
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-3/5 space-y-6">

              {/* Strength */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  {t('training.strengthTitle')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t('training.strengthText')}
                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    {t('training.strengthFact')}
                  </p>
                </div>
              </div>

              {/* Muscle Mass */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  {t('training.muscleTitle')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t('training.muscleText')}
                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    {t('training.muscleFact')}
                  </p>
                </div>
              </div>

            </div>

            <div className="lg:w-2/5 w-full">
              <img
                className="w-full h-[400px] lg:h-[500px] object-cover rounded-2xl shadow-lg border border-gray-100"
                src={kbSquat}
                alt="Trainer performing kettlebell squats"
                loading="lazy"
              />
            </div>
          </div>

          {/* Mobility, Flexibility, Balance */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

            {/* Mobility */}
            <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  {t('training.mobilityTitle')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t('training.mobilityText')}
                </p>
              </div>
              <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl mt-4">
                <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                  {t('training.mobilityFact')}
                </p>
              </div>
            </div>

            {/* Flexibility */}
            <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl flex flex-col justify-between">
              <div>
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  {t('training.flexibilityTitle')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t('training.flexibilityText')}
                </p>
              </div>
              <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl mt-4">
                <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                  {t('training.flexibilityFact')}
                </p>
              </div>
            </div>

          </div>

          {/* Balance & Cardio with Ball Lunge image */}
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            <div className="lg:w-2/5 w-full">
              <img
                className="w-full h-[320px] lg:h-[420px] object-cover rounded-2xl shadow-lg border border-gray-100"
                src={ball}
                alt="Trainer performing ball lunges for balance"
                loading="lazy"
              />
            </div>

            <div className="lg:w-3/5 space-y-6">

              {/* Balance */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  {t('training.balanceTitle')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t('training.balanceText')}
                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    {t('training.balanceFact')}
                  </p>
                </div>
              </div>

              {/* Cardiovascular Health */}
              <div className="bg-gray-50 border border-gray-150 p-6 rounded-2xl">
                <h3 className="text-lg font-extrabold text-[#162E3B] mb-3">
                  {t('training.cardioTitle')}
                </h3>
                <p className="text-base text-gray-700 leading-relaxed mb-4">
                  {t('training.cardioText')}
                </p>
                <div className="bg-[#FFD82D]/10 border-l-4 border-[#FFD82D] p-4 rounded-r-xl">
                  <p className="text-[#162E3B] font-extrabold text-base sm:text-lg">
                    {t('training.cardioFact')}
                  </p>
                </div>
              </div>
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                {t('training.aesthetics')}
              </p>
            </div>
          </div>

          {/* Conclusion / Summary block */}
          <div className="bg-[#162E3B] text-white rounded-2xl p-8 sm:p-10 shadow-lg space-y-6">

            <h3 className="text-xl sm:text-2xl font-extrabold text-[#FFD82D]">
              {t('training.conclusionTitle')}
            </h3>
            <p className="text-base sm:text-lg text-gray-200 leading-relaxed">
              {t('training.conclusionText1')}
            </p>
            <p className="text-base sm:text-lg font-bold text-white">
              {t('training.conclusionText2')}
            </p>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Training;
